import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { services } from '../data/mock';
import { Briefcase, Plane, Users, Sparkles } from 'lucide-react';

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/9e8b8adb-4702-43d4-a386-2eaf3df07374/images/da0a533c61bcb74f4223c68f8e3815e9368637c76e2d094c3f0f46931355b7f7.png";

const serviceIcons = [Briefcase, Plane, Users, Sparkles];
const slugMap = { 1: 'executive-travel', 2: 'airport-transfers', 3: 'family-chauffeur', 4: 'special-occasions' };

export const Services = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    }
  }, [location.hash]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('fade-in'); }),
      { threshold: 0.05, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.fade-element').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div data-testid="services-page" style={{ backgroundColor: '#0A0A0A' }}>

      {/* ── HERO ── */}
      <section data-testid="services-hero" className="relative overflow-hidden flex items-center justify-center" style={{ minHeight: '100vh' }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url(${HERO_IMG})`,
          filter: 'brightness(0.42) saturate(0.9) contrast(1.08)',
          scale: '1.05',
          animation: 'heroZoom 25s ease-in-out infinite alternate',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.2) 35%, rgba(0,0,0,0.15) 55%, rgba(0,0,0,0.75) 100%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 35%, rgba(194,168,120,0.06) 0%, transparent 55%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(135deg, rgba(0,0,0,0.3) 0%, transparent 40%, transparent 60%, rgba(0,0,0,0.3) 100%)',
        }} />
        <div className="grain-overlay absolute inset-0" style={{ opacity: 0.04 }} />

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto" style={{ paddingTop: '180px', paddingBottom: '180px' }}>
          <div className="fade-element" style={{ animationDelay: '0.2s' }}>
            <div className="gold-divider mx-auto" style={{ marginBottom: '32px', opacity: 0.7 }} />
            <p style={{ fontSize: '11px', color: 'rgba(194,168,120,0.55)', letterSpacing: '5px', textTransform: 'uppercase', fontWeight: 300, marginBottom: '36px' }}>
              Our Services
            </p>
          </div>
          <div className="fade-element" style={{ animationDelay: '0.4s' }}>
            <h1 data-testid="services-hero-heading" className="font-serif" style={{
              fontSize: 'clamp(40px, 5.5vw, 68px)', fontWeight: 500, color: '#FAFAFA',
              marginBottom: '32px', lineHeight: 1.08, textShadow: '0 2px 40px rgba(0,0,0,0.5)',
            }}>
              Premium Chauffeur Services, Delivered with Care
            </h1>
          </div>
          <div className="fade-element" style={{ animationDelay: '0.6s' }}>
            <p style={{ fontSize: '18px', color: 'rgba(220,220,220,0.65)', fontWeight: 300, lineHeight: 1.8, maxWidth: '520px', margin: '0 auto 56px' }}>
              Tailored transportation solutions for every journey
            </p>
          </div>
          <div className="fade-element" style={{ animationDelay: '0.8s' }}>
            <button
              data-testid="hero-cta-btn"
              onClick={() => navigate('/contact')}
              style={{
                backgroundColor: '#C2A878', color: '#0A0A0A', padding: '15px 40px',
                fontSize: '12px', fontWeight: 500, letterSpacing: '1.2px', textTransform: 'uppercase',
                border: 'none', cursor: 'pointer', transition: 'all 400ms ease',
                boxShadow: '0 4px 24px rgba(194,168,120,0.15)',
              }}
              onMouseEnter={(e) => { e.target.style.backgroundColor = '#D4BA8A'; e.target.style.boxShadow = '0 6px 32px rgba(194,168,120,0.25)'; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = '#C2A878'; e.target.style.boxShadow = '0 4px 24px rgba(194,168,120,0.15)'; }}
            >
              Book Your Ride
            </button>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0" style={{ height: '160px', background: 'linear-gradient(to top, #0A0A0A, transparent)' }} />
      </section>

      {/* ── SERVICE BLOCKS — Editorial Alternating Layout ── */}
      {services.map((service, i) => {
        const isEven = i % 2 === 0;
        const Icon = serviceIcons[i] || Briefcase;
        return (
          <section
            key={service.id}
            id={slugMap[service.id]}
            data-testid={`service-block-${service.id}`}
            className="relative overflow-hidden"
            style={{ backgroundColor: '#0A0A0A', scrollMarginTop: '120px' }}
          >
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2" style={{ minHeight: '700px' }}>

                {/* IMAGE SIDE */}
                <div className={`relative overflow-hidden fade-element ${isEven ? '' : 'lg:order-2'}`} style={{ minHeight: '400px' }}>
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-[1200ms] ease-out"
                    style={{
                      backgroundImage: `url(${service.image})`,
                      filter: 'brightness(0.65) saturate(0.9) contrast(1.05)',
                    }}
                    onMouseEnter={(e) => { e.target.style.transform = 'scale(1.04)'; }}
                    onMouseLeave={(e) => { e.target.style.transform = 'scale(1)'; }}
                  />
                  {/* Depth overlays on image */}
                  <div className="absolute inset-0" style={{
                    background: isEven
                      ? 'linear-gradient(90deg, transparent 50%, rgba(10,10,10,0.95) 100%)'
                      : 'linear-gradient(270deg, transparent 50%, rgba(10,10,10,0.95) 100%)',
                  }} />
                  <div className="absolute inset-0" style={{
                    background: 'linear-gradient(180deg, rgba(10,10,10,0.3) 0%, transparent 30%, transparent 70%, rgba(10,10,10,0.3) 100%)',
                  }} />
                  {/* Gold corner accent */}
                  <div className="absolute" style={{
                    ...(isEven ? { bottom: '40px', right: '40px' } : { bottom: '40px', left: '40px' }),
                    width: '48px', height: '48px',
                    borderBottom: '1px solid rgba(194,168,120,0.2)',
                    ...(isEven ? { borderRight: '1px solid rgba(194,168,120,0.2)' } : { borderLeft: '1px solid rgba(194,168,120,0.2)' }),
                  }} />
                </div>

                {/* CONTENT SIDE */}
                <div className={`relative flex items-center fade-element ${isEven ? '' : 'lg:order-1'}`} style={{ padding: 'clamp(48px, 6vw, 96px)' }}>
                  {/* Ambient glow */}
                  <div className="absolute inset-0" style={{
                    background: isEven
                      ? 'radial-gradient(ellipse at 30% 50%, rgba(194,168,120,0.03) 0%, transparent 50%)'
                      : 'radial-gradient(ellipse at 70% 50%, rgba(194,168,120,0.03) 0%, transparent 50%)',
                  }} />

                  <div className="relative z-10" style={{ maxWidth: '520px' }}>
                    {/* Icon */}
                    <div style={{
                      width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      backgroundColor: 'rgba(194,168,120,0.04)', border: '1px solid rgba(194,168,120,0.1)',
                      marginBottom: '32px',
                    }}>
                      <Icon size={20} strokeWidth={1.5} style={{ color: '#C2A878', opacity: 0.65 }} />
                    </div>

                    {/* Thin gold line */}
                    <div style={{ width: '32px', height: '1px', backgroundColor: 'rgba(194,168,120,0.25)', marginBottom: '28px' }} />

                    <h2 className="font-serif" style={{
                      fontSize: 'clamp(26px, 3vw, 38px)', fontWeight: 400, color: '#F5F5F5',
                      marginBottom: '28px', lineHeight: 1.2,
                    }}>
                      {service.title}
                    </h2>
                    <p style={{
                      fontSize: '15px', color: 'rgba(204,204,204,0.55)', fontWeight: 300,
                      lineHeight: 2, marginBottom: '28px',
                    }}>
                      {service.description}
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '40px' }}>
                      {service.features.map((f, j) => (
                        <div key={j} className="flex items-center" style={{ gap: '12px' }}>
                          <div style={{ width: '4px', height: '4px', backgroundColor: 'rgba(194,168,120,0.35)', borderRadius: '50%', flexShrink: 0 }} />
                          <span style={{ fontSize: '14px', color: 'rgba(225,225,225,0.8)', fontWeight: 300 }}>{f}</span>
                        </div>
                      ))}
                    </div>
                    <button
                      data-testid={`book-service-${service.id}`}
                      onClick={() => navigate('/contact')}
                      className="group"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '10px',
                        backgroundColor: 'transparent', color: '#C2A878',
                        padding: '13px 30px', fontSize: '12px', fontWeight: 400,
                        letterSpacing: '1px', textTransform: 'uppercase',
                        border: '1px solid rgba(194,168,120,0.2)',
                        cursor: 'pointer', transition: 'all 400ms ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(194,168,120,0.06)';
                        e.currentTarget.style.borderColor = 'rgba(194,168,120,0.45)';
                        e.currentTarget.style.boxShadow = '0 4px 24px rgba(194,168,120,0.08)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.borderColor = 'rgba(194,168,120,0.2)';
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      Book This Service
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {i < services.length - 1 && (
              <div style={{ position: 'relative', width: '100%', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(194,168,120,0.08) 20%, rgba(194,168,120,0.08) 80%, transparent)' }} />
            )}
          </section>
        );
      })}

      {/* ── OUR PROMISE — Cinematic Glass Section ── */}
      <section data-testid="service-promise" className="relative overflow-hidden" style={{ minHeight: '700px' }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url(${HERO_IMG})`,
          filter: 'brightness(0.15) saturate(0.5)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(10,10,10,0.97) 0%, rgba(10,10,10,0.55) 35%, rgba(10,10,10,0.55) 65%, rgba(10,10,10,0.97) 100%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 30% 50%, rgba(194,168,120,0.05) 0%, transparent 45%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(135deg, rgba(0,0,0,0.25) 0%, transparent 40%, transparent 60%, rgba(0,0,0,0.25) 100%)',
        }} />
        <div className="grain-overlay absolute inset-0" style={{ opacity: 0.04 }} />

        <div className="max-w-6xl mx-auto relative z-10" style={{ padding: '12rem 1.5rem' }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 items-start" style={{ gap: '80px' }}>

            {/* Left: Heading */}
            <div className="fade-element">
              <div className="gold-divider" style={{ marginBottom: '28px', opacity: 0.6 }} />
              <h2 className="font-serif" style={{
                fontSize: 'clamp(32px, 4vw, 48px)', fontWeight: 500, color: '#FAFAFA', lineHeight: 1.15, marginBottom: '28px',
                textShadow: '0 2px 30px rgba(0,0,0,0.4)',
              }}>
                Our Promise to You
              </h2>
              <p style={{ fontSize: '16px', color: 'rgba(220,220,220,0.55)', lineHeight: 1.9, fontWeight: 300, maxWidth: '420px' }}>
                Every service we provide is rooted in the same core values: respect, reliability, and genuine care for your wellbeing.
              </p>
            </div>

            {/* Right: Glass Promise Cards */}
            <div>
              {[
                { title: 'Consistency', desc: 'Same driver, same care, every journey. We build trust through reliability, not just service.' },
                { title: 'Safety', desc: 'Your wellbeing is always our first priority. From vehicle maintenance to driving standards, nothing is compromised.' },
                { title: 'Discretion', desc: 'Your privacy is always respected. Conversations, schedules, and personal details remain confidential.' },
              ].map((item, i) => (
                <div
                  key={i}
                  className="fade-element"
                  style={{
                    padding: '36px 32px',
                    marginBottom: i < 2 ? '16px' : '0',
                    backgroundColor: 'rgba(14,14,14,0.5)',
                    backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                    border: '1px solid rgba(194,168,120,0.06)',
                    transition: 'border-color 600ms ease, box-shadow 600ms ease, transform 400ms ease',
                    position: 'relative',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(194,168,120,0.16)';
                    e.currentTarget.style.boxShadow = '0 12px 48px rgba(0,0,0,0.3)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(194,168,120,0.06)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Gold top accent */}
                  <div style={{
                    position: 'absolute', top: 0, left: '15%', right: '15%', height: '1px',
                    background: 'linear-gradient(90deg, transparent, rgba(194,168,120,0.12), transparent)',
                  }} />
                  <h3 className="font-serif" style={{ fontSize: '20px', fontWeight: 400, color: '#C2A878', marginBottom: '12px', opacity: 0.8 }}>
                    {item.title}
                  </h3>
                  <p style={{ fontSize: '14px', color: 'rgba(225,225,225,0.8)', lineHeight: 1.9, fontWeight: 300 }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center fade-element" style={{ marginTop: '96px' }}>
            <button
              data-testid="services-cta-btn"
              onClick={() => navigate('/contact')}
              style={{
                backgroundColor: '#C2A878', color: '#0A0A0A', padding: '15px 44px',
                fontSize: '12px', fontWeight: 500, letterSpacing: '1.2px', textTransform: 'uppercase',
                border: 'none', cursor: 'pointer', transition: 'all 400ms ease',
                boxShadow: '0 4px 24px rgba(194,168,120,0.15)',
              }}
              onMouseEnter={(e) => { e.target.style.backgroundColor = '#D4BA8A'; e.target.style.boxShadow = '0 6px 32px rgba(194,168,120,0.25)'; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = '#C2A878'; e.target.style.boxShadow = '0 4px 24px rgba(194,168,120,0.15)'; }}
            >
              Get Started Today
            </button>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes heroZoom {
          0% { transform: scale(1.05); }
          100% { transform: scale(1.12); }
        }
      `}</style>
    </div>
  );
};
